import {
  BBBVideo,
  firstVerseNumber,
  getVerseRange,
} from '@deaf-bible-society-2-0/deafbible-metadata-utils';
import { useSelector } from 'react-redux';

import Item from '../Item';
import styles from './VerseRanges.module.css';
import useInternationalization from '../../../../hooks/useInternationalization';
import { RootState } from '../../../../store';
import useURLManipulator from '../../../../hooks/useURLManipulator';
import { getURLParams } from '../../../../utils/history';
import { hasIntroSegment, isIntroVerse } from '../../../../utils/timestamp';
import { introUrlVerseForVideo } from '../../../../utils/introResolver';

interface Props {
  darkMode: boolean;
  videos: BBBVideo[];
  selectedVideo: BBBVideo;
  selectVideo: (video: BBBVideo) => void;
  chapter: string;
}

const VerseRanges = ({
  darkMode,
  videos,
  selectedVideo,
  selectVideo,
  chapter,
}: Props): JSX.Element => {
  const { getIntlText } = useInternationalization();
  const rawTimestamps = useSelector((state: RootState) => state.timestamps.rawData);
  const { updateChapterVerse } = useURLManipulator();
  const { verse: currentVerse } = getURLParams();

  const hasIntroForVideo = (video: BBBVideo) => {
    const verseNumber = firstVerseNumber(video, chapter);
    const chapterNumber = parseInt(chapter, 10);
    const isIntroByTimestamp =
      !Number.isNaN(chapterNumber) && hasIntroSegment(rawTimestamps, chapterNumber, video.bbb_id);

    return isIntroByTimestamp || verseNumber === '0';
  };

  const hasIntro = videos.some(video => hasIntroForVideo(video));
  const onIntro = isIntroVerse(currentVerse);

  const introTargetVerse = (): string => {
    if (hasIntroForVideo(selectedVideo)) {
      return introUrlVerseForVideo(selectedVideo, chapter, videos);
    }

    const introVideo = videos.find(video => hasIntroForVideo(video));

    return introVideo !== undefined ? introUrlVerseForVideo(introVideo, chapter, videos) : '0';
  };

  return (
    <div className={styles.container}>
      {hasIntro && (
        <Item
          key="intro"
          isSelected={onIntro}
          darkMode={darkMode}
          onClick={() => updateChapterVerse(chapter, introTargetVerse())}
          text={getIntlText('intro')}
        />
      )}
      {videos.map(video => {
        const verseRange = getVerseRange(video, chapter);
        return (
          <Item
            key={video.bbb_id}
            isSelected={onIntro ? false : video.bbb_id === selectedVideo.bbb_id}
            darkMode={darkMode}
            onClick={() => selectVideo(video)}
            text={verseRange}
          />
        );
      })}
    </div>
  );
};

export default VerseRanges;
